import { useState, useMemo } from 'react';
import { Modal, Button } from '@components/ui';
import { useSelector } from 'react-redux';
import { getBlocksByConstructionId } from '@mocks/bloklar';
import { getApartmentsByBlockId } from '@mocks/daireler';

export default function SatisEkleModal({ isOpen, onClose, onSubmit }) {
    const { constructions } = useSelector((state) => state.construction);

    const [step, setStep] = useState(1);
    const [selectedConstruction, setSelectedConstruction] = useState(null);
    const [selectedBlock, setSelectedBlock] = useState(null);
    const [selectedApartment, setSelectedApartment] = useState(null);

    // Satış Form Verileri
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [salePrice, setSalePrice] = useState('');
    const [downPayment, setDownPayment] = useState('');
    const [installmentCount, setInstallmentCount] = useState('12');
    const [saleDate, setSaleDate] = useState(new Date().toISOString().split('T')[0]);

    // Kapatıldığında sıfırla
    const handleClose = () => {
        setStep(1);
        setSelectedConstruction(null);
        setSelectedBlock(null);
        setSelectedApartment(null);
        setCustomerName('');
        setCustomerPhone('');
        setSalePrice('');
        setDownPayment('');
        setInstallmentCount('12');
        onClose();
    };

    // Bağımlı veriler
    const blocks = useMemo(() =>
        selectedConstruction ? getBlocksByConstructionId(selectedConstruction.id) : [],
        [selectedConstruction]
    );

    const apartments = useMemo(() =>
        selectedBlock ? getApartmentsByBlockId(selectedBlock.id).filter(a => a.status === 'available') : [],
        [selectedBlock]
    );

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({
            constructionId: selectedConstruction.id,
            blockId: selectedBlock.id,
            apartmentId: selectedApartment.id,
            customerName,
            customerPhone,
            salePrice: parseFloat(salePrice),
            downPayment: parseFloat(downPayment) || 0,
            installmentCount: parseInt(installmentCount),
            saleDate
        });
        handleClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Yeni Daire Satışı"
            size="md"
        >
            <form onSubmit={handleSubmit} className="space-y-4">

                {/* 1. Adım: Proje Seçimi */}
                {step === 1 && (
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-text-secondary">Proje Seçin</label>
                        <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                            {constructions.map(c => (
                                <button
                                    type="button"
                                    key={c.id}
                                    onClick={() => {
                                        setSelectedConstruction(c);
                                        setStep(2);
                                    }}
                                    className="p-3 text-left rounded-lg border border-border-light hover:border-primary hover:bg-primary/5 transition-colors flex justify-between items-center"
                                >
                                    <span className="font-medium text-text-primary">{c.name}</span>
                                    <span className="text-xs text-text-muted">Seç &rarr;</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* 2. Adım: Daire Seçimi */}
                {step === 2 && (
                    <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
                        <div className="flex items-center justify-between pb-2 border-b border-border-light">
                            <h3 className="text-sm font-semibold text-primary">{selectedConstruction?.name}</h3>
                            <button type="button" onClick={() => setStep(1)} className="text-xs text-text-muted hover:text-text-primary">Geri</button>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-text-secondary mb-1">Blok Seçin</label>
                                <select
                                    className="w-full p-2 rounded-lg border border-border-light bg-bg-primary text-sm"
                                    onChange={(e) => {
                                        const block = blocks.find(b => b.id === e.target.value);
                                        setSelectedBlock(block);
                                        setSelectedApartment(null);
                                    }}
                                    value={selectedBlock?.id || ''}
                                >
                                    <option value="">Seçiniz</option>
                                    {blocks.map(b => (
                                        <option key={b.id} value={b.id}>{b.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-text-secondary mb-1">Boş Daireler</label>
                                <select
                                    className="w-full p-2 rounded-lg border border-border-light bg-bg-primary text-sm"
                                    disabled={!selectedBlock}
                                    onChange={(e) => {
                                        const apt = apartments.find(a => a.id === e.target.value);
                                        setSelectedApartment(apt);
                                    }}
                                    value={selectedApartment?.id || ''}
                                    required
                                >
                                    <option value="">Seçiniz</option>
                                    {apartments.map(a => (
                                        <option key={a.id} value={a.id}>
                                            Daire {a.number} ({a.netM2} m²)
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {selectedApartment && (
                            <div className="pt-4">
                                <Button type="button" onClick={() => setStep(3)} className="w-full text-sm font-medium" variant="primary">
                                    Devam Et: Müşteri Bilgileri
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {/* 3. Adım: Müşteri ve Finansal Bilgiler */}
                {step === 3 && (
                    <div className="space-y-4 animate-in slide-in-from-right-4 duration-300">
                        <div className="flex items-center justify-between pb-2 border-b border-border-light">
                            <div>
                                <div className="text-xs text-text-muted">{selectedConstruction?.name} / {selectedBlock?.name}</div>
                                <div className="font-bold text-text-primary">Daire {selectedApartment?.number} Satışı</div>
                            </div>
                            <button type="button" onClick={() => setStep(2)} className="text-xs text-text-muted hover:text-text-primary">Geri</button>
                        </div>

                        <div className="space-y-4">
                            <h4 className="text-xs font-bold text-text-tertiary uppercase tracking-wider border-b border-border-light pb-2">Müşteri Bilgileri</h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-text-secondary mb-1">Ad Soyad</label>
                                    <input
                                        type="text"
                                        required
                                        value={customerName}
                                        onChange={(e) => setCustomerName(e.target.value)}
                                        className="w-full p-2.5 rounded-lg border border-border-light bg-bg-primary text-sm focus:outline-none focus:border-primary"
                                        placeholder="Müşteri Adı"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-text-secondary mb-1">Telefon</label>
                                    <input
                                        type="text"
                                        value={customerPhone}
                                        onChange={(e) => setCustomerPhone(e.target.value)}
                                        className="w-full p-2.5 rounded-lg border border-border-light bg-bg-primary text-sm focus:outline-none focus:border-primary"
                                        placeholder="05..."
                                    />
                                </div>
                            </div>

                            <h4 className="text-xs font-bold text-text-tertiary uppercase tracking-wider border-b border-border-light pb-2 mt-6">Finansal Anlaşma</h4>

                            {/* Satış Bedeli */}
                            <div className="bg-bg-tertiary p-4 rounded-xl border border-border-light">
                                <label className="block text-sm font-semibold text-text-primary mb-2">Satış Bedeli (TL)</label>
                                <input
                                    type="number"
                                    required
                                    value={salePrice}
                                    onChange={(e) => setSalePrice(e.target.value)}
                                    className="w-full p-3 text-lg font-bold rounded-lg border border-border-light focus:border-primary outline-none"
                                    placeholder="0.00"
                                />
                            </div>

                            {/* Peşinat Detayları */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-3">
                                    <label className="block text-xs font-medium text-text-secondary">Peşinat Tutarı (TL)</label>
                                    <input
                                        type="number"
                                        value={downPayment}
                                        onChange={(e) => setDownPayment(e.target.value)}
                                        className="w-full p-2.5 rounded-lg border border-border-light bg-bg-primary text-sm focus:outline-none focus:border-primary"
                                        placeholder="0.00"
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label className="block text-xs font-medium text-text-secondary">Peşinat Ödeme Aracı</label>
                                    <select
                                        className="w-full p-2.5 rounded-lg border border-border-light bg-bg-primary text-sm focus:outline-none focus:border-primary"
                                        onChange={(e) => {
                                            if (e.target.value === 'cek') {
                                                // just for UI simulation purposes
                                            }
                                        }}
                                    >
                                        <option value="nakit">Nakit / Banka Transferi</option>
                                        <option value="cek">Alınan Çek</option>
                                        <option value="senet">Senet</option>
                                    </select>
                                </div>
                            </div>

                            <div className="bg-primary/5 p-3 rounded-lg border border-primary/20">
                                <p className="text-xs text-primary">
                                    * Eğer "Alınan Çek" veya "Senet" opsiyonlarından biri seçilirse, satış onaylandığında çek/senet detaylarını girmek üzere Portföy ekranına yönlendirileceksiniz.
                                </p>
                            </div>

                            {/* Taksit Detayları */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-3">
                                    <label className="block text-xs font-medium text-text-secondary">Kalan Tutar Vadesi (Ay)</label>
                                    <input
                                        type="number"
                                        min="1"
                                        placeholder="Örn: 15"
                                        value={installmentCount}
                                        onChange={(e) => setInstallmentCount(e.target.value)}
                                        className="w-full p-2.5 rounded-lg border border-border-light bg-bg-primary text-sm focus:outline-none focus:border-primary"
                                    />
                                    <p className="text-[10px] text-text-muted mt-1">* Taksitsiz nakit satışlar için 1 girebilirsiniz.</p>
                                </div>
                                <div className="space-y-3">
                                    <label className="block text-xs font-medium text-text-secondary">Taksit Ödeme Aracı</label>
                                    <select className="w-full p-2.5 rounded-lg border border-border-light bg-bg-primary text-sm focus:outline-none focus:border-primary">
                                        <option value="banka">Düzenli Havale / EFT</option>
                                        <option value="senet">Senet Düzenlenecek</option>
                                        <option value="cek">Vadeli Çekler (Sıralı)</option>
                                        <option value="kredi_karti">Kredi Kartı / Mail Order</option>
                                        <option value="nakit">Elden Nakit</option>
                                    </select>
                                </div>
                            </div>

                            <div className="pt-2">
                                <label className="block text-xs font-medium text-text-secondary mb-1">Sözleşme Tarihi</label>
                                <input
                                    type="date"
                                    required
                                    value={saleDate}
                                    onChange={(e) => setSaleDate(e.target.value)}
                                    className="w-full p-2.5 rounded-lg border border-border-light bg-bg-primary text-sm focus:outline-none focus:border-primary"
                                />
                            </div>
                        </div>

                        <div className="pt-6 flex gap-3 mt-4 border-t border-border-light">
                            <Button type="button" variant="outline" onClick={handleClose} className="flex-1 py-2.5">İptal</Button>
                            <Button type="submit" variant="success" className="flex-[2] py-2.5 font-bold shadow-sm">Satışı ve Ödeme Planını Onayla</Button>
                        </div>
                    </div>
                )}
            </form>
        </Modal>
    );
}
